import React, { useEffect, useState } from 'react';
import { Form } from 'antd';

import { FormattedMessage } from 'react-intl';
import Modal from 'components/Modal';
import globalMessages from 'translations/messages';
import { useMutation } from 'hooks/apollo';
import useFormatMessage from 'hooks/useFormatMessage';
import useGlobalContext from 'hooks/useGlobalContext';
import { Prompt } from 'react-router-dom';
import useUserConfirmation from 'hooks/useUserConfirmation';
import EditQuestion from './EditQuestion';
import EditBlock from './EditBlock';
import EditText from './EditText';

import createBlockMutation from './createBlock.gql';
import updateBlockMutation from './updateBlock.gql';
import createQuestionMutation from './createQuestion.gql';
import updateQuestionMutation from './updateQuestion.gql';
import createTextMutation from './createText.gql';
import updateTextMutation from './updateText.gql';

import messages from './messages';

function getDependencies(dependencies = []) {
  return dependencies.map(d => ({
    id: d.id,
    questionId: d.question?.id,
    optionIds: d.options?.map(o => o.id),
    answer: d.answer,
  }));
}

const getNodeType = (node) => {
  let type = 'Question';
  if (node.block) { type = 'Block'; }
  if (node.text) { type = 'Text'; }
  return type;
};

function getTranslatedArray(map, getter = (v) => v) {
  return map ? Object.entries(map).map(([surveyId, value]) => ({ surveyId, ...getter(value, surveyId) })) : undefined;
}

function toOptionInput(option, index) {
  return {
    id: option.id,
    title: option.title,
    value: option.value,
    active: option.active,
    position: index,
  };
}

function toQuestionInput(values) {
  const { types = [], tab, benchmark, options, translations } = values;

  const translationsOptions = {};
  const optionsData = options?.map((o, index) => {
    if (o.translations) {
      Object.entries(o.translations).forEach(([surveyId, translation]) => {
        if (!translationsOptions[surveyId]) {
          translationsOptions[surveyId] = [];
        }
        translationsOptions[surveyId].push({
          id: translation.id,
          title: translation.title,
          active: translation.active,
        });
      });
    }
    return toOptionInput(o, index);
  });

  return {
    categoryIds: [...types, ...(tab ? [tab] : []), ...(benchmark ? [benchmark] : [])],
    options: optionsData,
    title: values.title,
    description: values.description,
    dependencies: values.dependencies.filter(d => d.questionId),
    dependencyOperator: values.dependencyOperator,
    settings: {
      type: values.type,
      subType: values.subType,
      required: values.required,
      random: values.random,
      lockLastOption: values.lockLastOption,
      min: values.min,
      max: values.max,
      email: values.email,
      rows: values.rows,
    },
    translations: getTranslatedArray(translations, (v, surveyId) => ({
      ...v,
      options: translationsOptions[surveyId],
    })),
  };
}

function toCreateQuestionInput(position, surveyId, parentId, type, values) {
  return {
    position,
    surveyId,
    parentId,
    type,
    ...toQuestionInput(values),
  };
}

function toUpdateQuestionInput(id, values) {
  return {
    id,
    active: values.active,
    ...toQuestionInput(values),
  };
}

function toCreateTextInput(values) {
  return {
    ...values,
    translations: getTranslatedArray(values.translations),
  };
}

function toUpdateTextInput(id, values) {
  return {
    contentId: id,
    ...values,
    translations: getTranslatedArray(values.translations),
  };
}

const EditContentModal = ({ selectedNode, survey, questions, translations, onEdit, onCancel }) => {
  const { id, position, parentId, block, question, text, dependencyOperator } = selectedNode || {};
  const dependencies = getDependencies(selectedNode?.dependencies);
  const title = selectedNode && <FormattedMessage {...messages.editContent} values={{ type: getNodeType(selectedNode) }} />;
  const [form] = Form.useForm();
  const [isTouched, setIsTouched] = useState(false);
  const { message } = useGlobalContext();
  const getUserConfirmation = useUserConfirmation();
  const $t = useFormatMessage();
  const [createQuestion, { loading: creatingQuestion }] = useMutation(createQuestionMutation);
  const [updateQuestion, { loading: updatingQuestion }] = useMutation(updateQuestionMutation);

  useEffect(() => {
    form.resetFields();
    setIsTouched(false);
  }, [selectedNode]);

  const upsertQuestion = async (values) => {
    try {
      if (!id) {
        const input = toCreateQuestionInput(position, survey.id, parentId, question.settings.type, values);
        await createQuestion({ variables: { input } });
      } else {
        const input = toUpdateQuestionInput(question.id, values);
        await updateQuestion({ variables: { input } });
      }
      message.success($t(globalMessages.successfullFormFeedback));
      onEdit();
    } catch (e) {
      console.log(e);
      message.error($t(globalMessages.errorFormFeedback));
    }
  };

  const [createBlock, { loading: creatingBlock }] = useMutation(createBlockMutation);
  const [updateBlock, { loading: updatingBlock }] = useMutation(updateBlockMutation);
  const upsertBlock = async (values) => {
    try {
      if (!id) {
        await createBlock({ variables: { parentId, position, ...values } });
      } else {
        await updateBlock({ variables: { contentId: id, ...values } });
      }
      message.success($t(globalMessages.successfullFormFeedback));
      onEdit();
    } catch (e) {
      console.log(e);
      message.error($t(globalMessages.errorFormFeedback));
    }
  };

  const [createText, { loading: creatingText }] = useMutation(createTextMutation);
  const [updateText, { loading: updatingText }] = useMutation(updateTextMutation);
  const upsertText = async (values) => {
    try {
      if (!id) {
        const input = toCreateTextInput({ parentId, position, ...values });
        await createText({ variables: { input } });
      } else {
        const input = toUpdateTextInput(id, values);
        await updateText({ variables: { input } });
      }
      message.success($t(globalMessages.successfullFormFeedback));
      onEdit();
    } catch (e) {
      console.log(e);
      message.error($t(globalMessages.errorFormFeedback));
    }
  };

  const safeCancel = () => {
    if (isTouched) {
      getUserConfirmation($t(globalMessages.unsavedChanges), ok => { if (ok) onCancel(); });
    } else {
      onCancel();
    }
  };

  const loading = creatingQuestion || updatingQuestion || creatingBlock || updatingBlock || creatingText || updatingText;

  return (
    <Modal
      title={title}
      open={!!selectedNode}
      maskClosable={false}
      okText={<FormattedMessage {...globalMessages.save} />}
      onOk={form.submit}
      onCancel={safeCancel}
      fullWidth
      confirmLoading={loading}
    >
      <Form.Provider onFormChange={() => setIsTouched(true)}>
        <Prompt when={isTouched} message={$t(globalMessages.unsavedChanges)} />
        {block && (
        <EditBlock
          block={block}
          dependencies={dependencies}
          dependencyOperator={dependencyOperator}
          surveyQuestions={questions}
          onSubmit={upsertBlock}
          form={form}
        />
        )}
        {question && (
        <EditQuestion
          surveyContent={selectedNode}
          dependencies={dependencies}
          survey={survey}
          translations={translations}
          surveyQuestions={questions}
          onSubmit={upsertQuestion}
          form={form}
        />
        )}
        {text && (
        <EditText
          text={text}
          dependencies={dependencies}
          dependencyOperator={dependencyOperator}
          survey={survey}
          translations={translations}
          surveyQuestions={questions}
          onSubmit={upsertText}
          form={form}
        />
        )}
      </Form.Provider>
    </Modal>
  );
};

export default EditContentModal;
